import { useEffect, useMemo, useRef, useState } from "react";
import { Check, CreditCard, Landmark, Search, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useBankReconciliation } from "@/hooks/useBankReconciliation";
import { BankTransactionRow, JOURNAL_DRAG_TYPE } from "./BankTransactionRow";
import { AccountRail } from "./AccountRail";
import {
  BankReconciliationTabs,
  type BankReconciliationTab,
} from "./BankReconciliationTabs";
import {
  BankTransactionStatusRow,
  BankTransactionStatusTable,
} from "./BankTransactionStatusRow";
import { JournalEntryRow } from "./JournalEntryRow";
import { ReconciledMatchesList } from "./ReconciledMatchesList";
import { MatchSuggestionButton } from "./MatchSuggestionButton";
import { GRID_LINE, SpreadsheetHeaderRow } from "./TransactionSpreadsheetRow";
import { ROW_GRID, UnreconciledColumnHeaders } from "./UnreconciledColumnHeaders";
import { formatCurrency } from "../cashflow/CashflowContext";
import type { Currency } from "@/types";
import { formatAppDate } from "../../utils/date";
import type {
  BankAccount,
  BankReconciliationMatch,
  BankReconciliationTransaction,
  JournalEntry,
} from "@/types/bankReconciliation";

function buildAllBankTransactions(
  bankTransactions: BankReconciliationTransaction[],
  matches: BankReconciliationMatch[],
): BankReconciliationTransaction[] {
  const fromMatches = matches.map((match) => match.bankTransaction);
  return [...fromMatches, ...bankTransactions].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
}

function matchesSearch(text: string, amount: number, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (text.toLowerCase().includes(q)) return true;
  return Math.abs(amount).toFixed(2).includes(q.replace(/[^0-9.]/g, "") || " ");
}

function AccountSummary({ account }: { account: BankAccount }) {
  const Icon = account.accountType === "credit_card" ? CreditCard : Landmark;
  const detail = [
    account.institution,
    account.mask ? `···· ${account.mask}` : null,
    account.currency,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex min-w-0 items-center gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
        <Icon className="h-4.5 w-4.5" aria-hidden />
      </span>
      <div className="min-w-0">
        <h2 className="truncate text-base font-semibold text-slate-900">
          {account.label}
        </h2>
        <p className="truncate text-xs text-slate-500">
          {detail}
          {" · "}
          <span
            className={
              account.unreconciledCount === 0
                ? "font-medium text-emerald-600"
                : "font-medium text-amber-600"
            }
          >
            {account.unreconciledCount === 0
              ? "Fully reconciled"
              : `${account.unreconciledCount} to reconcile`}
          </span>
        </p>
      </div>
    </div>
  );
}

export function BankReconciliationWorkspace() {
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    null,
  );
  const {
    data,
    isLoading,
    error,
    createMatch,
    createMatches,
    deleteMatch,
    pushToErp,
  } = useBankReconciliation(selectedAccountId);
  const [selectedJournalId, setSelectedJournalId] = useState<string | null>(
    null,
  );
  const [dropTargetBankId, setDropTargetBankId] = useState<string | null>(null);
  const [draggingJournalId, setDraggingJournalId] = useState<string | null>(
    null,
  );
  const draggingJournalIdRef = useRef<string | null>(null);
  const [matchingBankId, setMatchingBankId] = useState<string | null>(null);
  const [matchError, setMatchError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<BankReconciliationTab>(
    "to-reconcile",
  );
  const [search, setSearch] = useState("");
  const [onlySuggestions, setOnlySuggestions] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  const accounts = useMemo(() => data?.bankAccounts ?? [], [data]);

  // Bookkeepers work one account at a time — select the first by default.
  useEffect(() => {
    if (selectedAccountId === null && accounts.length > 0) {
      setSelectedAccountId(accounts[0].id);
    }
  }, [accounts, selectedAccountId]);

  function handleSelectAccount(accountId: string) {
    setSelectedAccountId(accountId);
    setSelectedJournalId(null);
    setSearch("");
    setOnlySuggestions(false);
    setMatchError(null);
    setActiveTab("to-reconcile");
  }

  async function handleMatch(
    bankTransactionId: string,
    journalEntryId: string,
  ) {
    setMatchingBankId(bankTransactionId);
    setMatchError(null);
    try {
      await createMatch(bankTransactionId, journalEntryId);
      setSelectedJournalId(null);
    } catch (error) {
      setMatchError(
        error instanceof Error
          ? error.message
          : "Could not create match. Please try again.",
      );
    } finally {
      setMatchingBankId(null);
      setDropTargetBankId(null);
      setDraggingJournalId(null);
      draggingJournalIdRef.current = null;
    }
  }

  async function handleUnmatch(matchId: string) {
    setMatchError(null);
    try {
      await deleteMatch(matchId);
    } catch (error) {
      setMatchError(
        error instanceof Error ? error.message : "Could not unmatch.",
      );
    }
  }

  async function handlePushToErp(matchIds: string[]) {
    setMatchError(null);
    try {
      await pushToErp(matchIds);
    } catch (error) {
      setMatchError(
        error instanceof Error
          ? error.message
          : "Could not push to your accounting software. Please try again.",
      );
    }
  }

  function isJournalDrag(event: React.DragEvent): boolean {
    if (draggingJournalIdRef.current) return true;
    const types = Array.from(event.dataTransfer.types);
    return types.includes(JOURNAL_DRAG_TYPE) || types.includes("text/plain");
  }

  function handleRowDragOver(
    event: React.DragEvent<HTMLDivElement>,
    bankTransactionId: string,
  ) {
    if (!isJournalDrag(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDropTargetBankId(bankTransactionId);
  }

  function handleRowDrop(
    event: React.DragEvent<HTMLDivElement>,
    bankTransactionId: string,
  ) {
    event.preventDefault();
    const journalEntryId =
      event.dataTransfer.getData(JOURNAL_DRAG_TYPE) ||
      event.dataTransfer.getData("text/plain");
    if (!journalEntryId) return;
    void handleMatch(bankTransactionId, journalEntryId);
  }

  function renderReconciliationRow(
    bankTx: BankReconciliationTransaction,
    journalById: Map<string, JournalEntry>,
  ) {
    const suggestedJournal = bankTx.suggestedJournalEntryId
      ? journalById.get(bankTx.suggestedJournalEntryId)
      : undefined;
    const isDropTarget = dropTargetBankId === bankTx.id;
    const isMatching = matchingBankId === bankTx.id;

    return (
      <div
        key={bankTx.id}
        className={`${ROW_GRID} border-b ${GRID_LINE}`}
        onDragEnter={(event) => handleRowDragOver(event, bankTx.id)}
        onDragOver={(event) => handleRowDragOver(event, bankTx.id)}
        onDragLeave={() => {
          if (dropTargetBankId === bankTx.id) {
            setDropTargetBankId(null);
          }
        }}
        onDrop={(event) => handleRowDrop(event, bankTx.id)}
      >
        <div className={`min-h-0 border-r ${GRID_LINE}`}>
          <BankTransactionRow
            item={bankTx}
            isDropTarget={isDropTarget}
            isMatching={isMatching}
            canAcceptJournal={Boolean(selectedJournalId)}
            onAcceptJournal={() => {
              if (!selectedJournalId) return;
              void handleMatch(bankTx.id, selectedJournalId);
            }}
          />
        </div>

        <div
          className={`flex items-center justify-center border-r ${GRID_LINE}`}
        >
          {bankTx.suggestedJournalEntryId ? (
            <MatchSuggestionButton
              disabled={isMatching || bulkProgress !== null}
              onAccept={() => {
                if (!bankTx.suggestedJournalEntryId) return;
                void handleMatch(bankTx.id, bankTx.suggestedJournalEntryId);
              }}
            />
          ) : null}
        </div>

        <div className="min-h-0">
          {suggestedJournal ? (
            <JournalEntryRow item={suggestedJournal} readOnly />
          ) : (
            <div
              className={`flex items-center justify-center py-2 text-xs ${
                isDropTarget ? "text-emerald-600" : "text-slate-400"
              }`}
            >
              Drop journal entry
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isLoading && !data) {
    return (
      <p className="text-sm text-slate-500">Loading reconciliation data…</p>
    );
  }

  if (error && !data) {
    return (
      <p className="text-sm text-red-600">
        Failed to load reconciliation data.
      </p>
    );
  }

  const bankTransactions = data?.bankTransactions ?? [];
  const journalEntries = data?.journalEntries ?? [];
  const matches = data?.matches ?? [];
  const activeAccount =
    accounts.find((account) => account.id === selectedAccountId) ?? null;

  const journalById = new Map(journalEntries.map((entry) => [entry.id, entry]));
  const suggestedJournalIds = new Set(
    bankTransactions
      .map((tx) => tx.suggestedJournalEntryId)
      .filter((id): id is string => Boolean(id)),
  );

  // Suggestions first, then newest — the actionable rows lead.
  const sortedUnreconciled = [...bankTransactions].sort((a, b) => {
    const aSuggested = a.suggestedJournalEntryId ? 0 : 1;
    const bSuggested = b.suggestedJournalEntryId ? 0 : 1;
    if (aSuggested !== bSuggested) return aSuggested - bSuggested;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  const visibleUnreconciled = sortedUnreconciled.filter((tx) => {
    if (onlySuggestions && !tx.suggestedJournalEntryId) return false;
    return matchesSearch(tx.description, tx.amount, search);
  });

  const suggestionCount = bankTransactions.filter((tx) =>
    Boolean(tx.suggestedJournalEntryId),
  ).length;
  const visibleSuggestions = visibleUnreconciled.filter((tx) =>
    Boolean(tx.suggestedJournalEntryId),
  );

  const poolJournalEntries = journalEntries.filter(
    (entry) =>
      !suggestedJournalIds.has(entry.id) &&
      matchesSearch(
        `${entry.description} ${entry.accountName ?? ""} ${entry.reference ?? ""}`,
        entry.amount,
        search,
      ),
  );

  const allBankTransactions = buildAllBankTransactions(
    bankTransactions,
    matches,
  );
  const matchByBankId = new Map(
    matches.map((match) => [match.bankTransaction.id, match]),
  );
  const tabCounts: Record<BankReconciliationTab, number> = {
    "to-reconcile": bankTransactions.length,
    reconciled: matches.length,
    all: allBankTransactions.length,
  };

  async function handleAcceptAllSuggestions() {
    const pairs = visibleSuggestions
      .filter((tx) => tx.suggestedJournalEntryId)
      .map((tx) => ({
        bankTransactionId: tx.id,
        journalEntryId: tx.suggestedJournalEntryId as string,
      }));
    if (pairs.length === 0) return;

    setMatchError(null);
    setBulkProgress({ done: 0, total: pairs.length });
    try {
      const result = await createMatches(pairs, (done, total) =>
        setBulkProgress({ done, total }),
      );
      if (result.failed > 0) {
        setMatchError(
          `${result.failed} of ${pairs.length} matches failed${
            result.firstError ? ` — ${result.firstError}` : ""
          }`,
        );
      }
    } finally {
      setBulkProgress(null);
    }
  }

  return (
    <div className="flex w-full flex-col gap-5">
      {matchError ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {matchError}
        </p>
      ) : null}

      <AccountRail
        accounts={accounts}
        activeAccountId={selectedAccountId}
        onSelect={handleSelectAccount}
      />

      <section
        className={`flex min-h-0 flex-col overflow-hidden rounded-xl border ${GRID_LINE} bg-white`}
      >
        <header
          className={`flex flex-wrap items-center justify-between gap-3 border-b ${GRID_LINE} px-4 py-3`}
        >
          {activeAccount ? (
            <AccountSummary account={activeAccount} />
          ) : (
            <h2 className="text-base font-semibold text-slate-900">
              All accounts
            </h2>
          )}
          <BankReconciliationTabs
            active={activeTab}
            onChange={setActiveTab}
            counts={tabCounts}
          />
        </header>

        {activeTab === "to-reconcile" ? (
          <>
            <div
              className={`flex flex-wrap items-center gap-2 border-b ${GRID_LINE} px-4 py-2.5`}
            >
              <label className="relative min-w-0 flex-1 sm:max-w-xs">
                <Search
                  className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                  aria-hidden
                />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search description or amount…"
                  aria-label="Search transactions"
                  className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-8 pr-3 text-sm text-slate-800 outline-none ring-emerald-500 placeholder:text-slate-400 focus:ring-2"
                />
              </label>

              <button
                type="button"
                onClick={() => setOnlySuggestions((prev) => !prev)}
                aria-pressed={onlySuggestions}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                  onlySuggestions
                    ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                }`}
              >
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
                AI matches ({suggestionCount})
              </button>

              <div className="ml-auto">
                {bulkProgress ? (
                  <span className="text-xs font-medium tabular-nums text-slate-500">
                    Accepting {bulkProgress.done}/{bulkProgress.total}…
                  </span>
                ) : visibleSuggestions.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => void handleAcceptAllSuggestions()}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700"
                  >
                    <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden />
                    Accept all {visibleSuggestions.length} AI matches
                  </button>
                ) : null}
              </div>
            </div>

            <UnreconciledColumnHeaders />

            <div
              key={selectedAccountId ?? "all"}
              className="max-h-[30rem] min-h-0 flex-1 overflow-y-auto"
              onDragOver={(event) => {
                if (!isJournalDrag(event)) return;
                event.preventDefault();
              }}
            >
              {visibleUnreconciled.length === 0 ? (
                <p className="px-2 py-8 text-center text-sm text-slate-500">
                  {bankTransactions.length === 0
                    ? "Nothing left to reconcile in this account. 🎉"
                    : "No transactions match the current filters."}
                </p>
              ) : (
                visibleUnreconciled.map((bankTx) =>
                  renderReconciliationRow(bankTx, journalById),
                )
              )}

              {poolJournalEntries.length > 0 ? (
                <div className={`border-t ${GRID_LINE} px-4 py-4`}>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Journal entries without a bank match (
                    {poolJournalEntries.length})
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">
                    {selectedJournalId
                      ? "Selected — click a bank transaction row to match"
                      : "Click to select, or drag onto a row above"}
                  </p>
                  <div className={`mt-3 border ${GRID_LINE}`}>
                    <SpreadsheetHeaderRow />
                    {poolJournalEntries.map((item) => (
                      <div
                        key={item.id}
                        className={`border-b ${GRID_LINE} last:border-b-0`}
                      >
                        <JournalEntryRow
                          item={item}
                          selected={selectedJournalId === item.id}
                          isDragging={draggingJournalId === item.id}
                          onSelect={() =>
                            setSelectedJournalId((prev) =>
                              prev === item.id ? null : item.id,
                            )
                          }
                          onDragStart={() => {
                            draggingJournalIdRef.current = item.id;
                            setDraggingJournalId(item.id);
                          }}
                          onDragEnd={() => {
                            draggingJournalIdRef.current = null;
                            setDraggingJournalId(null);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </>
        ) : null}

        {activeTab === "reconciled" ? (
          <ReconciledMatchesList
            matches={matches}
            onUnmatch={(matchId) => void handleUnmatch(matchId)}
            onPush={handlePushToErp}
          />
        ) : null}

        {activeTab === "all" ? (
          allBankTransactions.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-500">
              No transactions in this account yet. Upload a statement in{" "}
              <Link
                to="/files-integrations"
                className="font-medium text-emerald-700 hover:underline"
              >
                Files &amp; Integrations
              </Link>
              .
            </p>
          ) : (
            <div className="max-h-[34rem] overflow-y-auto">
              <BankTransactionStatusTable>
                {allBankTransactions.map((transaction) => (
                  <BankTransactionStatusRow
                    key={transaction.id}
                    transaction={transaction}
                    match={matchByBankId.get(transaction.id)}
                    onUnmatch={(matchId) => void handleUnmatch(matchId)}
                  />
                ))}
              </BankTransactionStatusTable>
            </div>
          )
        ) : null}
      </section>
    </div>
  );
}
